<template>
  <div class="benchmarking-section">
    <h3 class="section-title">
      <i class="fas fa-chart-line"></i> Análise Comparativa
    </h3>
    
    <div class="benchmark-content">
      <div class="benchmark-chart-container">
        <h4 class="benchmark-title">
          <i class="fas fa-city"></i> Comparação com Destinos Similares
        </h4>
        <div class="chart-container">
          <canvas id="benchmarkChart" height="250"></canvas>
        </div>
      </div>
      
      <div class="benchmark-details">
        <div class="benchmark-tabs">
          <button 
            v-for="tab in benchmarkTabs" 
            :key="tab.id"
            class="tab-button" 
            :class="{ active: activeTab === tab.id }"
            @click="activeTab = tab.id"
          >
            <i :class="'fas ' + tab.icon"></i>
            {{ tab.name }}
          </button>
        </div>
        
        <div class="tab-content">
          <!-- Tab de Ranking -->
          <div v-if="activeTab === 'ranking'" class="ranking-content">
            <h4 class="ranking-title">Ranking de Destinos</h4>
            <p class="ranking-description">
              Posicionamento relativo entre destinos similares com base em avaliações dos visitantes.
            </p>
            <div class="ranking-list">
              <div 
                v-for="(item, index) in benchmarkData.ranking" 
                :key="'rank-'+index"
                class="ranking-item"
                :class="{ 'current-city': item.isCurrent }"
              >
                <div class="rank-position">{{ index + 1 }}</div>
                <div class="rank-info">
                  <div class="rank-city">{{ item.city }}</div>
                  <div class="rank-details">{{ item.country || 'Brasil' }}</div>
                </div>
                <div class="rank-score" :class="getRankScoreClass(index)">
                  {{ item.score.toFixed(1) }}
                </div>
              </div>
            </div>
          </div>
          
          <!-- Tab de Melhores Práticas -->
          <div v-if="activeTab === 'best-practices'" class="practices-content">
            <h4 class="practices-title">Melhores Práticas</h4>
            <p class="practices-description">
              Iniciativas e abordagens de destinos líderes que podem ser adaptadas localmente.
            </p>
            <div class="practices-list">
              <div class="practice-card" v-for="(practice, index) in benchmarkData.bestPractices" :key="'practice-'+index">
                <div class="practice-header">
                  <div class="practice-city">{{ practice.city }}</div>
                  <div class="practice-score">{{ practice.score.toFixed(1) }}</div>
                </div>
                <div class="practice-title">{{ practice.title }}</div>
                <p class="practice-description">{{ practice.description }}</p>
                <div class="practice-impact">
                  <span class="impact-label">Impacto Potencial:</span>
                  <div class="impact-stars">
                    <i 
                      class="fas fa-star" 
                      v-for="i in 5" 
                      :key="'star-'+i"
                      :class="{ 'active': i <= practice.impact }"
                    ></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Tab de Tendências -->
          <div v-if="activeTab === 'trends'" class="trends-content">
            <h4 class="trends-title">Tendências do Setor</h4>
            <p class="trends-description">
              Direções e movimentos observados no setor de {{ categoryName }} em destinos similares.
            </p>
            <div class="trends-list">
              <div class="trend-card" v-for="(trend, index) in benchmarkData.trends" :key="'trend-'+index">
                <div class="trend-icon">
                  <i :class="'fas ' + trend.icon"></i>
                </div>
                <div class="trend-content">
                  <div class="trend-title">{{ trend.title }}</div>
                  <p class="trend-description">{{ trend.description }}</p>
                  <div class="trend-adoption">
                    <span class="adoption-label">Taxa de Adoção:</span>
                    <div class="adoption-bar">
                      <div class="adoption-fill" :style="{ width: trend.adoption + '%' }"></div>
                    </div>
                    <span class="adoption-percentage">{{ trend.adoption }}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js'
Chart.register(...registerables)

export default {
  props: {
    categoryData: Object,
    categoryName: String
  },
  data() {
    return {
      benchmarkChart: null,
      activeTab: 'ranking',
      benchmarkTabs: [
        { id: 'ranking', name: 'Ranking', icon: 'fa-trophy' },
        { id: 'best-practices', name: 'Melhores Práticas', icon: 'fa-award' },
        { id: 'trends', name: 'Tendências', icon: 'fa-chart-line' }
      ],
      benchmarkData: {
        comparison: [],
        ranking: [],
        bestPractices: [],
        trends: []
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.generateBenchmarkData()
      this.createBenchmarkChart()
    })
  },
  beforeDestroy() {
    if (this.benchmarkChart) {
      this.benchmarkChart.destroy()
      this.benchmarkChart = null
    }
  },
  methods: {
    generateBenchmarkData() {
      // Gerar dados simulados de benchmark
      // Em uma implementação real, estes dados viriam de uma API ou banco de dados
      
      // Dados de comparação para o gráfico
      this.benchmarkData.comparison = [
        { city: 'Sua Cidade', score: this.getCurrentCityScore(), isCurrent: true },
        { city: 'Florianópolis', score: 4.2, isCurrent: false },
        { city: 'Porto de Galinhas', score: 4.5, isCurrent: false },
        { city: 'Gramado', score: 4.7, isCurrent: false },
        { city: 'Paraty', score: 4.0, isCurrent: false },
        { city: 'Búzios', score: 4.1, isCurrent: false }
      ]
      
      // Ranking de cidades
      this.benchmarkData.ranking = [
        { city: 'Gramado', country: 'Brasil', score: 4.7, isCurrent: false },
        { city: 'Porto de Galinhas', country: 'Brasil', score: 4.5, isCurrent: false },
        { city: 'Florianópolis', country: 'Brasil', score: 4.2, isCurrent: false },
        { city: 'Búzios', country: 'Brasil', score: 4.1, isCurrent: false },
        { city: 'Sua Cidade', country: 'Brasil', score: this.getCurrentCityScore(), isCurrent: true },
        { city: 'Paraty', country: 'Brasil', score: 4.0, isCurrent: false },
        { city: 'Maragogi', country: 'Brasil', score: 3.9, isCurrent: false },
        { city: 'Jericoacoara', country: 'Brasil', score: 3.8, isCurrent: false }
      ]
      
      // Ordenar o ranking por pontuação
      this.benchmarkData.ranking.sort((a, b) => b.score - a.score)
      
      // Melhores práticas
      this.benchmarkData.bestPractices = [
        {
          city: 'Gramado',
          score: 4.7,
          title: 'Padronização de Qualidade',
          description: 'Sistema de certificação e padronização de serviços com auditorias regulares e feedback público.',
          impact: 5
        },
        {
          city: 'Porto de Galinhas',
          score: 4.5,
          title: 'Capacitação Contínua',
          description: 'Programa de capacitação trimestral obrigatório para todos os profissionais do setor turístico.',
          impact: 4
        },
        {
          city: 'Florianópolis',
          score: 4.2,
          title: 'Incentivo à Inovação',
          description: 'Fundo de apoio para implementação de soluções inovadoras no setor, com foco em experiência do cliente.',
          impact: 4
        }
      ]
      
      // Tendências do setor
      this.benchmarkData.trends = [
        {
          title: 'Digitalização da Experiência',
          description: 'Integração de tecnologias digitais para enriquecer a experiência dos visitantes, como realidade aumentada e sistemas de reserva integrados.',
          icon: 'fa-mobile-alt',
          adoption: 78
        },
        {
          title: 'Sustentabilidade',
          description: 'Crescente adoção de práticas sustentáveis e ecoamigáveis, com redução de resíduos e certificações ambientais.',
          icon: 'fa-leaf',
          adoption: 65
        },
        {
          title: 'Personalização',
          description: 'Ofertas e experiências cada vez mais personalizadas, utilizando dados para adaptar serviços às preferências individuais.',
          icon: 'fa-user-cog',
          adoption: 52
        }
      ]
    },
    
    createBenchmarkChart() {
      const canvas = document.getElementById('benchmarkChart')
      if (!canvas) return
      
      // Destruir gráfico anterior se existir
      if (this.benchmarkChart) {
        this.benchmarkChart.destroy()
        this.benchmarkChart = null
      }
      
      const ctx = canvas.getContext('2d')
      
      const data = this.benchmarkData.comparison
      
      const colors = data.map(item => item.isCurrent ? '#324DDD' : '#94a3b8')
      
      this.benchmarkChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: data.map(item => item.city),
          datasets: [{
            label: 'Avaliação Média',
            data: data.map(item => item.score),
            backgroundColor: colors,
            borderColor: colors,
            borderWidth: 1,
            borderRadius: 6,
            barThickness: 40
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  return `Avaliação: ${context.parsed.y.toFixed(1)}/5`;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: false,
              min: 3.5,
              max: 5.0,
              ticks: {
                stepSize: 0.5
              },
              title: {
                display: true,
                text: 'Avaliação Média',
                font: {
                  size: 13,
                  weight: 'bold'
                }
              }
            }
          }
        }
      })
    },
    
    getCurrentCityScore() {
      // Em uma implementação real, isso utilizaria dados reais da cidade atual
      // Aqui estamos usando os dados da categoria para simular
      if (!this.categoryData || !this.categoryData.average_data || !this.categoryData.average_data.average_ratings) {
        return 4.0 // Valor padrão
      }
      
      return this.categoryData.average_data.average_ratings
    },
    
    getRankScoreClass(index) {
      if (index === 0) return 'gold'
      if (index === 1) return 'silver'
      if (index === 2) return 'bronze'
      return ''
    }
  }
}
</script>

<style scoped>
.benchmarking-section {
  margin-bottom: 30px;
}

.section-title {
  font-size: 20px;
  color: #0f172a;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.benchmark-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 25px;
}

.benchmark-chart-container, .benchmark-details {
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.benchmark-title {
  font-size: 16px;
  font-weight: 600;
  padding: 15px 20px;
  margin: 0;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 8px;
}

.chart-container {
  padding: 20px;
  height: 250px;
  position: relative;
}

.benchmark-tabs {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
}

.tab-button {
  padding: 15px 20px;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tab-button:hover {
  color: #324DDD;
  background: #f1f5f9;
}

.tab-button.active {
  color: #324DDD;
  box-shadow: inset 0 -3px 0 #324DDD;
  background: white;
}

.tab-content {
  padding: 20px;
}

.ranking-title, .practices-title, .trends-title {
  font-size: 16px;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 10px;
  color: #0f172a;
}

.ranking-description, .practices-description, .trends-description {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 20px;
}

.ranking-list {
  max-height: 320px;
  overflow-y: auto;
}

.ranking-item {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 8px;
  background: #f8fafc;
  transition: transform 0.2s;
}

.ranking-item:hover {
  transform: translateX(5px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.ranking-item.current-city {
  background: #e0e7ff;
  border-left: 3px solid #324DDD;
}

.rank-position {
  font-size: 18px;
  font-weight: 700;
  color: #94a3b8;
  width: 30px;
  text-align: center;
}

.ranking-item:nth-child(1) .rank-position {
  color: #f59e0b;
}

.ranking-item:nth-child(2) .rank-position {
  color: #94a3b8;
}

.ranking-item:nth-child(3) .rank-position {
  color: #b45309;
}

.rank-info {
  flex: 1;
  padding: 0 15px;
}

.rank-city {
  font-weight: 600;
  color: #0f172a;
}

.rank-details {
  font-size: 12px;
  color: #64748b;
}

.rank-score {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  background: #e2e8f0;
  padding: 4px 10px;
  border-radius: 4px;
}

.rank-score.gold {
  background: #fef3c7;
  color: #b45309;
}

.rank-score.silver {
  background: #f1f5f9;
  color: #475569;
}

.rank-score.bronze {
  background: #fff7ed;
  color: #c2410c;
}

.practices-list, .trends-list {
  display: grid;
  gap: 15px;
  max-height: 320px;
  overflow-y: auto;
}

.practice-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 15px;
  transition: transform 0.2s;
}

.practice-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
}

.practice-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.practice-city {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
}

.practice-score {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  background: #e2e8f0;
  padding: 2px 8px;
  border-radius: 4px;
}

.practice-title {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 8px;
}

.practice-description {
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
  margin-bottom: 12px;
}

.practice-impact {
  display: flex;
  align-items: center;
  gap: 10px;
}

.impact-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
}

.impact-stars {
  display: flex;
  gap: 2px;
}

.impact-stars i {
  color: #cbd5e1;
  font-size: 12px;
}

.impact-stars i.active {
  color: #f59e0b;
}

.trend-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  gap: 15px;
  transition: transform 0.2s;
}

.trend-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
}

.trend-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #e0e7ff;
  color: #324DDD;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.trend-content {
  flex: 1;
}

.trend-title {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 8px;
}

.trend-description {
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
  margin-bottom: 12px;
}

.trend-adoption {
  display: flex;
  align-items: center;
  gap: 10px;
}

.adoption-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  min-width: 100px;
}

.adoption-bar {
  height: 8px;
  background: #e2e8f0;
  flex: 1;
  border-radius: 4px;
  overflow: hidden;
}

.adoption-fill {
  height: 100%;
  background: #324DDD;
  border-radius: 4px;
}

.adoption-percentage {
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
  min-width: 40px;
  text-align: right;
}

@media (max-width: 1200px) {
  .benchmark-content {
    grid-template-columns: 1fr;
  }
}
</style> 